import { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormFieldValue } from '@/features/submission_forms/types';

interface UseAutosaveProps {
  form: UseFormReturn;
  onSave: () => Promise<void>;
  savedAnswers?: Record<string, FormFieldValue>;
  enabled?: boolean;
  debounceMs?: number;
  initialLastSaved?: string | null;
  onFormChange?: () => void; // Callback when form changes (before save)
  staleTimeoutMs?: number; // Max ms to show syncing before giving up (default 30000)
}

interface UseAutosaveReturn {
  isSaving: boolean;
  lastSaved: string | null;
  saveError: string | null;
  isStale: boolean; // Indicates if there are unsaved changes
}

/**
 * Hook to automatically save form changes after a debounce period
 */
export const useAutosave = ({
  form,
  onSave,
  savedAnswers = {},
  enabled = true,
  debounceMs = 2000,
  initialLastSaved = null,
  onFormChange,
  staleTimeoutMs = 30_000,
}: UseAutosaveProps): UseAutosaveReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(initialLastSaved);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const staleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousValuesRef =
    useRef<Record<string, FormFieldValue>>(savedAnswers);

  useEffect(() => {
    if (!enabled) return;

    // Subscribe to form changes
    const subscription = form.watch(currentValues => {
      // Check if any values have actually changed
      const hasChanges = Object.entries(currentValues).some(
        ([key, value]) => previousValuesRef.current[key] !== value
      );

      if (!hasChanges) return;

      // Mark as stale when changes are detected
      setIsStale(true);

      // Safety timeout: if the save never completes, stop showing the syncing
      // indicator rather than leaving it frozen indefinitely.
      if (staleTimeoutRef.current) clearTimeout(staleTimeoutRef.current);
      staleTimeoutRef.current = setTimeout(() => {
        setIsStale(false);
      }, staleTimeoutMs);

      // Call the form change callback if provided
      onFormChange?.();

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for autosave
      timeoutRef.current = setTimeout(async () => {
        setIsSaving(true);
        setSaveError(null);

        try {
          await onSave();
          setLastSaved(new Date().toISOString());
          previousValuesRef.current = form.getValues();
          // Clear stale flag after successful save
          setIsStale(false);
          if (staleTimeoutRef.current) {
            clearTimeout(staleTimeoutRef.current);
            staleTimeoutRef.current = null;
          }
        } catch (error) {
          setSaveError(
            error instanceof Error ? error.message : 'Failed to save changes'
          );
        } finally {
          setIsSaving(false);
        }
      }, debounceMs);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (staleTimeoutRef.current) clearTimeout(staleTimeoutRef.current);
    };
  }, [form, onSave, enabled, debounceMs, onFormChange, staleTimeoutMs]);

  return {
    isSaving,
    lastSaved,
    saveError,
    isStale,
  };
};
